import { differenceInCalendarDays, format, isWeekend } from 'date-fns'

import { formatDate, parseISODate } from '@avantstay/backoffice-core'

import { AvailableDatesRange, DateKind } from '@new/domains/fastBookingsTapeChart/__types'

export const parseDate = (d: string | Date | undefined | null) => {
  if (!d) {
    return 0
  }

  if (d instanceof Date) {
    return d
  }

  return new Date(d.length === 10 ? `${d}T00:00:00` : d)
}

export const shortISODate = (it: Date | number | string | undefined) =>
  it ? format(parseISODate(it), 'yyyy-MM-dd') : ''

export const isSameSet = (
  a: Set<unknown> | null | undefined,
  b?: Set<unknown> | null | undefined,
) => a && b && a.size === b.size && Array.from(a.values()).every(itemFromA => b.has(itemFromA))

export function getColumnOffsets({
  startDate,
  endDate,
  visibleDates,
}: {
  startDate: string
  endDate: string
  visibleDates: Date[]
}) {
  const $firstVisibleDate = visibleDates[0]
  const startDiff =
    Math.round(differenceInCalendarDays(parseISODate(startDate), $firstVisibleDate) + 1) * 2
  const endDiff =
    Math.round(differenceInCalendarDays(parseISODate(endDate), $firstVisibleDate) + 1) * 2

  const minCol = 1
  const maxCol = visibleDates.length * 2 + 1
  return {
    colStart: Math.max(minCol, startDiff),
    colEnd: Math.min(maxCol, endDiff),
    continuing: startDiff < minCol,
    willContinue: endDiff > maxCol,
  }
}

export const memoizeLocalStorage = <T extends (...args: any[]) => R, R extends any>(
  fnKey: string,
  fn: T,
  ttl: number,
): T => {
  // Fetch localstorage or init new object
  const cache = JSON.parse('{}')

  async function executeAndCacheFn(_fn: T, args: unknown[], argsKey: string) {
    const result = await _fn(...args)
    // reset the cache value
    cache[fnKey] = {
      ...cache[fnKey],
      [argsKey]: { expiration: Date.now() + ttl, result },
    }
    localStorage.setItem(fnKey, JSON.stringify(cache))
  }

  return (async (...args: unknown[]) => {
    // Note: JSON.stringify is non-deterministic,
    // consider something like json-stable-stringify to avoid extra cache misses
    const argsKey = JSON.stringify(args)

    if (!cache[fnKey] || !cache[fnKey][argsKey] || cache[fnKey][argsKey].expiration <= Date.now()) {
      await executeAndCacheFn(fn, args, argsKey)
    }

    return cache[fnKey][argsKey].result
  }) as T
}

export const getTapeChartDateKind = (d: Date | string | number) => {
  if (isWeekend(parseISODate(d))) {
    return DateKind.weekend
  }

  return DateKind.regular
}

export const minifyXml = (svgCode: string) =>
  // @ts-ignore
  svgCode.trim().replace(/(^|>)([\n\s\t]+)(<|$)/gs, '$1$3')

export const createRandomClassName = () => `_${Math.random().toString(33).substring(2)}`

const IGNORE_BLOCKS_VALUE = '1'
const DATE_RANGE_SEPARATOR = '_'

export const parseDateRange = (value: string): AvailableDatesRange | undefined => {
  if (!value) {
    return undefined
  }
  const [checkIn, checkOut, noFiltering] = value.split(DATE_RANGE_SEPARATOR)
  return {
    checkIn: formatDate(checkIn),
    checkOut: formatDate(checkOut),
    ignoreBlocks: noFiltering === IGNORE_BLOCKS_VALUE,
  }
}

export const parseDateRangeParam = (value: AvailableDatesRange) => {
  if (!value) {
    return undefined
  }

  const { checkIn, checkOut } = value

  return checkIn === checkOut
    ? formatDate(checkIn, 'MMM dd, yyyy')
    : `between ${formatDate(checkIn, 'MMM dd, yyyy')} and ${formatDate(checkOut, 'MMM dd, yyyy')}`
}

export const formatDateRange = (value?: AvailableDatesRange): string => {
  if (!value) {
    return ''
  }

  const rangeFields = [value.checkIn, value.checkOut]
  if (value.ignoreBlocks) {
    rangeFields.push(IGNORE_BLOCKS_VALUE)
  }

  return rangeFields.join(DATE_RANGE_SEPARATOR)
}
